import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
// 'contact' (= "Gespräch anfragen") ist raus — doppelt mit dem grünen Header-CTA
// "Kostenlose Potenzialanalyse buchen". Stattdessen Login zum Kundenportal direkt
// nach 'blog' (Insights). Sebastian-Klarstellung 2026-06-02.
const navItems = ['home', 'services', 'method', 'blog'];
const sectionIds = { home: 'hero', services: 'services', method: 'road', blog: 'blog' };

export default function Header() {
  const { lang, toggleLang, t } = useLanguage();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mcActive, setMcActive] = useState(false);
  const isOnHomepage = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Aktive Masterclass abfragen (für Conditional Nav-Link)
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/masterclass`).then((r) => {
      if (cancelled) return;
      const d = r.data || {};
      // Link nur zeigen wenn echtes Event existiert (id gesetzt) und nicht vorbei
      setMcActive(!!d.id && d.status !== 'past');
    }).catch(() => {/* still */});
    return () => { cancelled = true; };
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      const headerHeight = 80;
      const top = el.getBoundingClientRect().top + window.pageYOffset - headerHeight;
      try {
        window.scrollTo({ top, behavior: 'smooth' });
      } catch {
        window.scrollTo(0, top);
      }
    }, 100);
  };

  return (
    <motion.header
      data-testid="header-nav"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/80 backdrop-blur-xl border-b border-[#e5e5e5] shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        {/* Logo */}
        <button
          data-testid="header-logo"
          onClick={() => scrollTo('hero')}
          className="flex items-center gap-3 group"
        >
          <img
            src="/assets/logo.webp"
            alt="ENBET Consulting – KI Beratung für Retail und stationäre Geschäfte"
            width="200"
            height="120"
            fetchPriority="high"
            loading="eager"
            decoding="async"
            className={`h-10 w-auto transition-all duration-300 ${scrolled ? 'invert' : ''}`}
          />
        </button>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navItems.map((item) => (
            isOnHomepage ? (
              <button
                key={item}
                data-testid={`nav-${item}`}
                onClick={() => scrollTo(sectionIds[item])}
                className={`text-[13px] font-medium uppercase tracking-[0.15em] transition-colors duration-300 hover:text-[#befa0a] ${
                  scrolled ? 'text-[#1f1f1f]' : 'text-white/80'
                }`}
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {t(`nav.${item}`)}
              </button>
            ) : (
              <Link
                key={item}
                to={`/#${sectionIds[item]}`}
                data-testid={`nav-${item}`}
                className={`text-[13px] font-medium uppercase tracking-[0.15em] transition-colors duration-300 hover:text-[#befa0a] ${
                  scrolled ? 'text-[#1f1f1f]' : 'text-white/80'
                }`}
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {t(`nav.${item}`)}
              </Link>
            )
          ))}
          {/* Login → Kundenportal */}
          <Link
            to="/portal"
            data-testid="nav-login"
            className={`text-[13px] font-medium uppercase tracking-[0.15em] transition-colors duration-300 hover:text-[#befa0a] ${
              scrolled ? 'text-[#1f1f1f]' : 'text-white/80'
            }`}
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            {lang === 'de' ? 'Login' : 'Login'}
          </Link>
          {mcActive && (
            <Link
              to="/ki-masterclass"
              data-testid="nav-masterclass"
              className={`relative text-[13px] font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
                scrolled ? 'text-[#e07a2f] hover:text-[#1f1f1f]' : 'text-[#e07a2f] hover:text-white'
              }`}
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#e07a2f] animate-pulse" />
                Masterclass
              </span>
            </Link>
          )}
        </nav>

        {/* Lang Toggle + CTA + Mobile */}
        <div className="flex items-center gap-4">
          <button
            data-testid="header-cta"
            onClick={() => scrollTo('booking')}
            className={`hidden md:inline-block text-[12px] font-bold uppercase tracking-[0.12em] px-5 py-2 transition-all duration-300 ${
              scrolled
                ? 'bg-[#befa0a] text-[#0a0a0a] hover:bg-[#1f1f1f] hover:text-white'
                : 'bg-[#befa0a] text-[#0a0a0a] hover:bg-white'
            }`}
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            {lang === 'de' ? 'Kostenlose Potenzialanalyse buchen' : 'Book free potential analysis'}
          </button>
          <button
            data-testid="lang-toggle"
            onClick={toggleLang}
            className={`text-[13px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 border transition-all duration-300 ${
              scrolled
                ? 'border-[#1f1f1f] text-[#1f1f1f] hover:bg-[#1f1f1f] hover:text-white'
                : 'border-white/40 text-white hover:bg-white hover:text-[#1f1f1f]'
            }`}
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            {lang === 'de' ? 'EN' : 'DE'}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden transition-colors ${scrolled ? 'text-[#1f1f1f]' : 'text-white'}`}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#0a0a0a] border-t border-[#333]"
          >
            <nav className="flex flex-col px-6 py-6 gap-4">
              {navItems.map((item) => (
                isOnHomepage ? (
                  <button
                    key={item}
                    data-testid={`mobile-nav-${item}`}
                    onClick={() => scrollTo(sectionIds[item])}
                    className="text-white/80 text-left text-sm uppercase tracking-[0.15em] hover:text-[#befa0a] transition-colors"
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                  >
                    {t(`nav.${item}`)}
                  </button>
                ) : (
                  <Link
                    key={item}
                    to={`/#${sectionIds[item]}`}
                    data-testid={`mobile-nav-${item}`}
                    onClick={() => setMobileOpen(false)}
                    className="text-white/80 text-left text-sm uppercase tracking-[0.15em] hover:text-[#befa0a] transition-colors"
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                  >
                    {t(`nav.${item}`)}
                  </Link>
                )
              ))}
              {/* Login → Kundenportal (Mobile) */}
              <Link
                to="/portal"
                data-testid="mobile-nav-login"
                onClick={() => setMobileOpen(false)}
                className="text-white/80 text-left text-sm uppercase tracking-[0.15em] hover:text-[#befa0a] transition-colors"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                Login
              </Link>
              {mcActive && (
                <Link
                  to="/ki-masterclass"
                  data-testid="mobile-nav-masterclass"
                  onClick={() => setMobileOpen(false)}
                  className="text-[#e07a2f] text-left text-sm uppercase tracking-[0.15em] font-bold hover:text-[#befa0a] transition-colors inline-flex items-center gap-2"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#e07a2f] animate-pulse" />
                  Masterclass
                </Link>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
